import {
  faAnglesDown,
  faAnglesRight
} from '@awesome.me/kit-5de77b2c02/icons/classic/regular'
import { faRotateRight } from '@awesome.me/kit-5de77b2c02/icons/classic/solid'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import cx from 'classnames'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { isOpenFinRuntime } from '../../containers/OpenFin/helpers'
import { createOrUpdateAlertFilter } from '../../store/alertFilter/actions'
import { getAlertFilter } from '../../store/alertFilter/selectors'
import {
  AlertFilter,
  AndOr,
  AndOrStrings,
  GreaterOrLessThan,
  GreaterOrLessThanStrings,
  PriceType
} from '../../store/alertFilter/types'
import {
  AllCouponTypes,
  AllRatings,
  AllRatingValues,
  AllSectors,
  AllSecurityTypes,
  CountryCodes,
  CouponType,
  CouponTypeValues,
  CurrencyValues,
  defaultFilter as defaultFilterType,
  Filter,
  MaturitiesValues,
  MaturityGroups,
  Rating,
  RatingsGroups,
  Sectors,
  SecurityType,
  SecurityTypeValues
} from '../../store/filter/types'
import { useAppDispatch, useAppSelector } from '../../store/types'
import { getWatchList } from '../../store/watchList/selectors'
import { WatchList } from '../../store/watchList/types'
import Checkbox from '../Checkbox'
import ComponentFooter from '../ComponentFooter/ComponentFooter'
import GenericDropdownMenu from '../GenericDropdownMenu/GenericDropdownMenu'
import GenericDropdownMenuMulti from '../GenericDropdownMenu/GenericDropdownMenuMulti'

import styles from './AlertFilter.module.scss'

export interface Props {
  filterId: number
  closeFunc: () => void
}

interface AlertPropertyCounts {
  identifiers: string
  lists: string
  filters: string
  sector: string
  maturity: string
  secType: string
  couponType: string
  ratings: string
  currency: string
}

const defaultFilter: AlertFilter = {
  id: 0,
  name: '',
  watchlists: [],
  alertWhenBidTopped: false,
  alertWhenBidCountered: false,
  alertWhenNewBid: false,
  alertWhenNewOffer: false,
  alertWhenOfferCountered: false,
  alertWhenOfferCut: false,
  alertWhenTradingNow: false,
  alertPrice: 0,
  alertPriceType: PriceType.Spread,
  alertSize: 0,
  muted: false,
  priceSizeAndOr: AndOr.And,
  alertWhenPriceGreaterOrLessThan: GreaterOrLessThan.GreaterThan,
  alertWhenSizeGreaterOrLessThan: GreaterOrLessThan.GreaterThan,
  usePriceSizeCriteria: false,
  filter: defaultFilterType,
  includesMineWatchlist: false,
  all: true
}
const createPropertyCount = (count: number) => {
  if (count) {
    return ` (${count})`
  }
  return ''
}

const createFilterPropertyCount = (selectedList: any[], valuesList: any[]) => {
  if (selectedList.length < valuesList.length) {
    return createPropertyCount(selectedList.length)
  }
  return ''
}

const sectors = Object.values(Sectors)

const emptyWatchlistArray: WatchList[] = []

const getInitalWatchlistSelection = (
  filter: AlertFilter | undefined,
  watchlists: WatchList[]
) => {
  const initialWatchlists: string[] = []
  if (!filter) {
    return initialWatchlists
  }
  filter.watchlists.forEach((wl) => {
    const watchlistItem = watchlists.find((obj) => obj.id === wl)
    if (watchlistItem) {
      initialWatchlists.push(watchlistItem.name)
    }
  })
  if (filter.includesMineWatchlist) {
    initialWatchlists.push('Mine')
  }
  return initialWatchlists
}
const AlertFilterEdit: FC<Props> = ({ filterId, closeFunc }) => {
  const [bondListOpen, setBondListOpen] = useState(false)
  const [watchlistOpen, setWatchlistListOpen] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [errorText, setErrorText] = useState('')
  const watchlists = useAppSelector(getWatchList) ?? emptyWatchlistArray
  const alertFilter = useAppSelector(getAlertFilter)(filterId) ?? defaultFilter

  const reset = () => {
    setAlertWhenBidTopped(false)
    setAlertWhenNewBid(false)
    setPriceHits('')
    setAllBondsChecked(false)
    setSelectedSectors(AllSectors.map((s) => Sectors[s]))
    setSelectedMaturities(MaturitiesValues)
    setSelectedSecurityTypes(
      AllSecurityTypes.map((st) => SecurityTypeValues[st])
    )
    setSelectedCouponTypes(AllCouponTypes.map((st) => CouponTypeValues[st]))
    setSelectedRatings(AllRatings.map((r) => AllRatingValues[r]))
    setSelectedCurrencies(CurrencyValues.map((c) => c.toString()))
    setAlertWhenBidTopped(false)
    setAlertWhenOfferCut(false)
    setAlertWhenTradingNow(false)
    setAlertWhenNewBid(false)
    setAlertWhenNewOffer(false)
    setAlertWhenBidCountered(false)
    setAlertWhenOfferCountered(false)
    setAndIfChecked(false)
    setSizeHits('')
    setSizeGreaterOrLessThan(GreaterOrLessThanStrings[0])
    setPriceGreaterOrLessThan(GreaterOrLessThanStrings[0])
    setPriceType(PriceType[PriceType.Price])
    setPriceSizeAndOrValue(AndOrStrings[AndOr.And])
    setSelectedWatchlists([])
    setSecuritiesIdentifiers('')
  }

  const [alertFilterData, setAlertFilterData] = useState(alertFilter)

  useEffect(() => {
    if (JSON.stringify(alertFilter) !== JSON.stringify(alertFilterData)) {
      setAlertFilterData(alertFilter)
      const af = alertFilter
      setAlertWhenBidTopped(af.alertWhenBidTopped)
      setAlertWhenNewBid(af.alertWhenNewBid)
      setPriceHits(
        af.alertPrice ? (af.alertPrice !== 0 ? af.alertPrice : '') : ''
      )
      setSelectedSectors(af.filter.sectors.map((s) => Sectors[s]))
      setRuleName(af.name)
      setSelectedMaturities(af.filter.maturities)
      setSelectedSecurityTypes(
        af.filter.securityTypes.map((st) => SecurityTypeValues[st])
      )
      setSelectedCouponTypes(
        af.filter.couponTypes.map((st) => CouponTypeValues[st])
      )
      setAllBondsChecked(af.all)
      setSelectedRatings(af.filter.ratings.map((r) => AllRatingValues[r]))
      setSelectedCurrencies(af.filter.currencies.map((c) => c.toString()))
      setAlertWhenBidTopped(af.alertWhenBidTopped)
      setAlertWhenOfferCut(af.alertWhenOfferCut)
      setAlertWhenTradingNow(af.alertWhenTradingNow)
      setAlertWhenNewBid(af.alertWhenNewBid)
      setAlertWhenNewOffer(af.alertWhenNewOffer)
      setAlertWhenBidCountered(af.alertWhenBidCountered)
      setAlertWhenOfferCountered(af.alertWhenOfferCountered)
      setAndIfChecked(af.usePriceSizeCriteria)
      const displaySz =
        af.alertSize !== undefined
          ? af.alertSize !== 0
            ? af.alertSize
            : ''
          : ''
      setSizeHits(displaySz)
      const newlySelectedWatchlists = [] as string[]
      af.watchlists.forEach((wl) => {
        const watchlistItem = watchlists.find((obj) => obj.id === wl)
        if (watchlistItem) {
          newlySelectedWatchlists.push(watchlistItem.name)
        }
      })
      if (af.includesMineWatchlist) {
        newlySelectedWatchlists.push('Mine')
      }
      setSelectedWatchlists(newlySelectedWatchlists)
      setSecuritiesIdentifiers(af.filter.securitiesAndTickers.join('\n'))
      setSizeGreaterOrLessThan(
        GreaterOrLessThanStrings[af.alertWhenSizeGreaterOrLessThan]
      )
      setPriceGreaterOrLessThan(
        GreaterOrLessThanStrings[af.alertWhenPriceGreaterOrLessThan]
      )
      setPriceType(PriceType[af.alertPriceType])
      setPriceSizeAndOrValue(AndOrStrings[af.priceSizeAndOr])
    }
  }, [alertFilter, alertFilterData, watchlists])

  const [selectedWatchlists, setSelectedWatchlists] = useState(
    getInitalWatchlistSelection(alertFilter, watchlists)
  )

  const dispatch = useAppDispatch()

  const toggleBondListOpen = useCallback(() => {
    setBondListOpen((value) => !value)
    setWatchlistListOpen(false)
    setFiltersOpen(false)
  }, [])
  const toggleWatchlistOpen = useCallback(() => {
    setWatchlistListOpen((value) => !value)
    setBondListOpen(false)
    setFiltersOpen(false)
  }, [])
  const toggleFiltersOpen = useCallback(() => {
    setFiltersOpen((value) => !value)
    setBondListOpen(false)
    setWatchlistListOpen(false)
  }, [])
  const [ruleName, setRuleName] = useState(alertFilter.name)
  const [selectedSectors, setSelectedSectors] = useState(
    alertFilter.filter.sectors.map((s) => Sectors[s])
  )
  const [selectedMaturities, setSelectedMaturities] = useState(
    alertFilter.filter.maturities
  )
  const [selectedSecurityTypes, setSelectedSecurityTypes] = useState(
    alertFilter.filter.securityTypes.map((st) => SecurityTypeValues[st])
  )
  const [selectedCouponTypes, setSelectedCouponTypes] = useState(
    alertFilter.filter.couponTypes.map((ct) => CouponTypeValues[ct])
  )
  const [allBondsChecked, setAllBondsChecked] = useState(alertFilter.all)
  const [selectedRatings, setSelectedRatings] = useState(
    alertFilter.filter.ratings.map((r) => AllRatingValues[r])
  )
  const [selectedCurrencies, setSelectedCurrencies] = useState(
    alertFilter.filter.currencies.map((c) => c.toString())
  )
  const [securitiesIdentifiers, setSecuritiesIdentifiers] = useState(
    alertFilter.filter.securitiesAndTickers.join('\n')
  )
  const [alertWhenBidTopped, setAlertWhenBidTopped] = useState(
    alertFilter.alertWhenBidTopped
  )
  const [alertWhenOfferCut, setAlertWhenOfferCut] = useState(
    alertFilter.alertWhenOfferCut
  )
  const [alertWhenTradingNow, setAlertWhenTradingNow] = useState(
    alertFilter.alertWhenTradingNow
  )
  const [alertWhenNewBid, setAlertWhenNewBid] = useState(
    alertFilter.alertWhenNewBid
  )
  const [alertWhenNewOffer, setAlertWhenNewOffer] = useState(
    alertFilter.alertWhenNewOffer
  )
  const [alertWhenBidCountered, setAlertWhenBidCountered] = useState(
    alertFilter.alertWhenBidCountered
  )
  const [alertWhenOfferCountered, setAlertWhenOfferCountered] = useState(
    alertFilter.alertWhenOfferCountered
  )
  const [andIfChecked, setAndIfChecked] = useState(
    alertFilter.usePriceSizeCriteria
  )

  const [priceGreaterOrLessThan, setPriceGreaterOrLessThan] = useState(
    GreaterOrLessThanStrings[alertFilter.alertWhenPriceGreaterOrLessThan]
  )

  const [sizeGreaterOrLessThan, setSizeGreaterOrLessThan] = useState(
    GreaterOrLessThanStrings[alertFilter.alertWhenSizeGreaterOrLessThan]
  )

  const [priceSizeAndOrValue, setPriceSizeAndOrValue] = useState(
    AndOrStrings[alertFilter.priceSizeAndOr]
  )
  const [priceType, setPriceType] = useState(
    PriceType[alertFilter.alertPriceType]
  )

  const displaySize =
    alertFilter.alertSize !== undefined
      ? alertFilter.alertSize !== 0
        ? alertFilter.alertSize
        : ''
      : ''
  const [sizeHits, setSizeHits] = useState(displaySize)
  const displayPrice =
    alertFilter.alertPrice !== undefined
      ? alertFilter.alertPrice !== 0
        ? alertFilter.alertPrice
        : ''
      : ''
  const [priceHits, setPriceHits] = useState(displayPrice)

  const alertPropertyCounts = useMemo<AlertPropertyCounts>(() => {
    const identifiers = createPropertyCount(
      securitiesIdentifiers ? securitiesIdentifiers.split('\n').length : 0
    )
    const lists = createPropertyCount(selectedWatchlists.length)

    const sector = createFilterPropertyCount(selectedSectors, sectors)
    const maturity = createFilterPropertyCount(
      selectedMaturities,
      MaturitiesValues
    )
    const secType = createFilterPropertyCount(
      selectedSecurityTypes,
      SecurityTypeValues
    )
    const couponType = createFilterPropertyCount(
      selectedCouponTypes,
      CouponTypeValues
    )
    const ratings = createFilterPropertyCount(selectedRatings, AllRatingValues)
    const currency = createFilterPropertyCount(
      selectedCurrencies,
      CurrencyValues
    )
    // add up how many filters have counts > 0
    const filterCount = [
      sector,
      maturity,
      secType,
      couponType,
      ratings,
      currency
    ].reduce((prev, displayCount) => (displayCount.length ? prev + 1 : prev), 0)

    const filters = createPropertyCount(filterCount)

    return {
      identifiers,
      lists,
      sector,
      maturity,
      secType,
      couponType,
      ratings,
      currency,
      filters
    }
  }, [
    securitiesIdentifiers,
    selectedWatchlists,
    selectedSectors,
    selectedMaturities,
    selectedSecurityTypes,
    selectedCouponTypes,
    selectedRatings,
    selectedCurrencies
  ])

  const alertPropertyStyle = useCallback(
    (property: keyof typeof alertPropertyCounts) => {
      return alertPropertyCounts[property] ? styles.notDefault : ''
    },
    [alertPropertyCounts]
  )

  const updateSecuritiesIdentifiers = (
    evt: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setSecuritiesIdentifiers(evt.target.value)
    if (evt.target.value !== '') {
      setAllBondsChecked(false)
    }
  }

  const updateWatchlists = (items: string[]) => {
    if (items.length > 0) {
      setAllBondsChecked(false)
    }
    setSelectedWatchlists(items)
  }

  const updateRuleName = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setRuleName(evt.target.value)
  }

  const allBondsCheckChanged = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setAllBondsChecked(evt.target.checked)
    setSecuritiesIdentifiers('')
    setSelectedWatchlists([])
  }

  const alertWhenOfferCutChanged = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAlertWhenOfferCut(evt.target.checked)
  }

  const alertWhenBidToppedChanged = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAlertWhenBidTopped(evt.target.checked)
  }

  const alertWhenNewBidChanged = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setAlertWhenNewBid(evt.target.checked)
  }

  const alertWhenNewOfferChanged = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAlertWhenNewOffer(evt.target.checked)
  }

  const andIfCheckedChanged = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setAndIfChecked(evt.target.checked)
  }

  const alertWhenBidCounteredChanged = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAlertWhenBidCountered(evt.target.checked)
  }

  const alertWhenOfferCounteredChanged = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAlertWhenOfferCountered(evt.target.checked)
  }

  const alertWhenTradingNowChanged = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAlertWhenTradingNow(evt.target.checked)
  }

  const sizeHitsChanged = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(evt.target.value)
      setSizeHits(val)
      if (val > 0) {
        setAndIfChecked(true)
      }
    },
    []
  )

  const priceHitsChanged = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(evt.target.value)
      setPriceHits(val)
      if (val > 0) {
        setAndIfChecked(true)
      }
    },
    []
  )

  // todo: save this filter
  const apply = () => {
    // convert selectedSectors to Sector enum
    const sectorsToSave = selectedSectors.map((s) => {
      for (const [key, value] of Object.entries(Sectors)) {
        if (value === s) {
          return Number(key)
        }
      }
    })

    const maturitiesToSave = selectedMaturities

    const securityTypesToSave = selectedSecurityTypes.map((st) => {
      const key = st.replace(/ /g, '_')
      return SecurityType[key as keyof typeof SecurityType]
    })
    let couponTypesToSave = AllCouponTypes
    if (selectedCouponTypes.length > 0) {
      couponTypesToSave = selectedCouponTypes.map((ct) => {
        const key = ct.replace(/ /g, '_')
        return CouponType[key as keyof typeof CouponType]
      })
    } else {
      setSelectedCouponTypes(AllCouponTypes.map((st) => CouponTypeValues[st]))
    }

    const ratingToSave = selectedRatings.map((r) => {
      const key = r.replace('+', 'Plus').replace('-', 'Minus')
      return Rating[key as keyof typeof Rating]
    })

    const currenciesToSave = selectedCurrencies

    const securitiesIdentifiersToSave = securitiesIdentifiers
      .split('\n')
      .filter((element) => element !== '')

    const name = ruleName
    const filter = {
      sectors: sectorsToSave,
      maturities: maturitiesToSave,
      securityTypes: securityTypesToSave,
      couponTypes: couponTypesToSave,
      currencies: currenciesToSave,
      ratings: ratingToSave,
      securitiesAndTickers: securitiesIdentifiersToSave,
      countries: CountryCodes
    } as Filter

    const watchlistsToSave = [] as number[]
    selectedWatchlists.forEach((wl) => {
      const watchlistItem = watchlists.find((obj) => obj.name === wl)
      if (watchlistItem) {
        watchlistsToSave.push(watchlistItem.id as number)
      }
    })

    const alrtFilter = {
      filter,
      watchlists: watchlistsToSave, // todo
      name,
      alertWhenBidTopped,
      alertWhenOfferCut,
      alertWhenNewBid,
      alertWhenNewOffer,
      alertWhenTradingNow,
      alertWhenBidCountered,
      alertWhenOfferCountered,
      alertSize: Number(sizeHits),
      muted: alertFilter?.muted ?? false,
      usePriceSizeCriteria: andIfChecked,
      priceSizeAndOr: priceSizeAndOrValue === 'And' ? AndOr.And : AndOr.Or,
      alertWhenPriceGreaterOrLessThan:
        priceGreaterOrLessThan === '≥'
          ? GreaterOrLessThan.GreaterThan
          : GreaterOrLessThan.LessThan,
      alertWhenSizeGreaterOrLessThan:
        sizeGreaterOrLessThan === '≥'
          ? GreaterOrLessThan.GreaterThan
          : GreaterOrLessThan.LessThan,
      alertPrice: Number(priceHits),
      alertPriceType: PriceType[priceType as keyof typeof PriceType],
      id: alertFilterData.id,
      includesMineWatchlist: selectedWatchlists.indexOf('Mine') > -1,
      all: allBondsChecked
    }

    if (
      (alrtFilter.alertWhenNewBid || alrtFilter.alertWhenNewOffer) &&
      alrtFilter.all &&
      !alertPropertyCounts.filters
    ) {
      setErrorText(
        'Error: Cannot select All Bonds for a New Bid or New Offer alert'
      )
    } else {
      dispatch(createOrUpdateAlertFilter(alrtFilter))
      closeFunc()
    }
  }

  return (
    <>
      <div className={cx(styles.alertFilterWrapper, styles.editFields)}>
        <div className={styles.menuSection}>
          <label>Name</label>
          <div className={styles.actionButton} onClick={reset}>
            Reset
            <FontAwesomeIcon icon={faRotateRight} flip="horizontal" />
          </div>
        </div>

        <div className={cx(styles.addMargin, styles.sectionContainer)}>
          <input
            type="text"
            className={styles.NameInput}
            placeholder="Name"
            value={ruleName}
            onChange={updateRuleName}
            name="ruleName"
            id="ruleName"
            autoComplete="off"
          />
        </div>

        <div className={styles.menuSection}>
          <label>Apply Alert Using:</label>
          <Checkbox
            locator={'allBondsCheck'}
            checked={allBondsChecked}
            onChange={allBondsCheckChanged}
            disabled={false}
          >
            All Bonds
          </Checkbox>
        </div>

        <div className={cx(styles.addMargin, styles.sectionContainer)}>
          <div
            className={cx(
              styles.FilterMethod,
              alertPropertyStyle('identifiers')
            )}
          >
            <div
              className={styles.FilterMethodHeader}
              onClick={toggleBondListOpen}
            >
              <span className={styles.AlertFilterMethodText}>
                TYPE / PASTE BONDS MANUALLY{alertPropertyCounts.identifiers}
              </span>

              <FontAwesomeIcon
                icon={bondListOpen ? faAnglesDown : faAnglesRight}
              />
            </div>

            {bondListOpen && (
              <div className={styles.BondList}>
                <p>SECURITY</p>
                <textarea
                  placeholder="Enter Ticker, CUSIP, or ISIN"
                  rows={5}
                  value={securitiesIdentifiers}
                  onChange={updateSecuritiesIdentifiers}
                ></textarea>
              </div>
            )}
          </div>
          <div className={cx(styles.FilterMethod, alertPropertyStyle('lists'))}>
            <div
              className={styles.FilterMethodHeader}
              onClick={toggleWatchlistOpen}
            >
              <span className={styles.AlertFilterMethodText}>
                LISTS{alertPropertyCounts.lists}
              </span>
              <FontAwesomeIcon
                icon={watchlistOpen ? faAnglesDown : faAnglesRight}
              />
            </div>
            {watchlistOpen && (
              <div className={styles.Watchlists}>
                <GenericDropdownMenuMulti
                  placeholder="Watchlists"
                  setSelectedItems={updateWatchlists}
                  options={['Mine'].concat(watchlists.map((wl) => wl.name))}
                  selectedItems={selectedWatchlists}
                  selectId={'AlertWatchlists'}
                  openRight={true}
                  className=""
                />
              </div>
            )}
          </div>
          <div
            className={cx(styles.FilterMethod, alertPropertyStyle('filters'))}
          >
            <div
              className={styles.FilterMethodHeader}
              onClick={toggleFiltersOpen}
            >
              <span className={styles.AlertFilterMethodText}>
                FILTERS{`${alertPropertyCounts.filters}`}
              </span>
              <FontAwesomeIcon
                icon={filtersOpen ? faAnglesDown : faAnglesRight}
              />
            </div>

            {filtersOpen && (
              <div>
                <GenericDropdownMenuMulti
                  placeholder={`Sectors${alertPropertyCounts.sector}`}
                  selectedItems={selectedSectors}
                  setSelectedItems={setSelectedSectors}
                  options={sectors}
                  selectId={'AlertSector'}
                  openRight={true}
                  className={alertPropertyStyle('sector')}
                />
                <GenericDropdownMenuMulti
                  placeholder={`Maturity${alertPropertyCounts.maturity}`}
                  groups={MaturityGroups}
                  selectedItems={selectedMaturities}
                  options={MaturitiesValues}
                  setSelectedItems={setSelectedMaturities}
                  selectId={'AlertMaturity'}
                  openRight={true}
                  className={alertPropertyStyle('maturity')}
                />
                <GenericDropdownMenuMulti
                  placeholder={`Security Type${alertPropertyCounts.secType}`}
                  selectedItems={selectedSecurityTypes}
                  options={SecurityTypeValues}
                  setSelectedItems={setSelectedSecurityTypes}
                  selectId={'AlertSecurityType'}
                  openRight={false}
                  className={alertPropertyStyle('secType')}
                />
                <GenericDropdownMenuMulti
                  placeholder={`Coupon Type${alertPropertyCounts.couponType}`}
                  selectedItems={selectedCouponTypes}
                  options={CouponTypeValues}
                  setSelectedItems={setSelectedCouponTypes}
                  selectId={'AlertCouponType'}
                  openRight={true}
                  className={alertPropertyStyle('couponType')}
                />
                <GenericDropdownMenuMulti
                  placeholder={`Ratings${alertPropertyCounts.ratings}`}
                  groups={RatingsGroups}
                  selectedItems={selectedRatings}
                  options={AllRatingValues}
                  setSelectedItems={setSelectedRatings}
                  selectId="AlertRating"
                  openRight={true}
                  className={alertPropertyStyle('ratings')}
                />
                <GenericDropdownMenuMulti
                  placeholder={`Currency${alertPropertyCounts.currency}`}
                  selectedItems={selectedCurrencies}
                  options={CurrencyValues}
                  setSelectedItems={setSelectedCurrencies}
                  selectId={'AlertCurrency'}
                  openRight={false}
                  className={alertPropertyStyle('currency')}
                />
              </div>
            )}
          </div>
        </div>

        <div className={styles.menuSection}>
          <label>Alert Me When:</label>
        </div>
        <div className={cx(styles.addMargin, styles.sectionContainer)}>
          <table className={styles.AlertMeWhenTable}>
            <tbody>
              <tr>
                <WebOnlyCheckboxCell
                  alertWhenNewBidOffer={alertWhenNewBid}
                  alertWhenNewBidOfferChanged={alertWhenNewBidChanged}
                  labelText={'New Bid'}
                />
                <td>
                  <Checkbox
                    locator={'myBidTopped'}
                    checked={alertWhenBidTopped}
                    onChange={alertWhenBidToppedChanged}
                    disabled={false}
                  >
                    My Bid Topped
                  </Checkbox>
                </td>
                <td>
                  <Checkbox
                    locator={'myBidCountered'}
                    checked={alertWhenBidCountered}
                    onChange={alertWhenBidCounteredChanged}
                    disabled={false}
                  >
                    My Bid Countered
                  </Checkbox>
                </td>
              </tr>
              <tr>
                <WebOnlyCheckboxCell
                  alertWhenNewBidOffer={alertWhenNewOffer}
                  alertWhenNewBidOfferChanged={alertWhenNewOfferChanged}
                  labelText={'New Offer'}
                />
                <td>
                  <Checkbox
                    locator={'myOfferCut'}
                    checked={alertWhenOfferCut}
                    onChange={alertWhenOfferCutChanged}
                    disabled={false}
                  >
                    My Offer Cut
                  </Checkbox>
                </td>
                <td>
                  <Checkbox
                    locator={'myOfferCountered'}
                    checked={alertWhenOfferCountered}
                    onChange={alertWhenOfferCounteredChanged}
                    disabled={false}
                  >
                    My Offer Countered
                  </Checkbox>
                </td>
              </tr>
              <tr>
                <td>
                  <Checkbox
                    locator={'tradingNow'}
                    checked={alertWhenTradingNow}
                    onChange={alertWhenTradingNowChanged}
                    disabled={false}
                  >
                    Trading on BondsPro
                  </Checkbox>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className={styles.menuSection}>
          <Checkbox
            locator={'andIf'}
            checked={andIfChecked}
            onChange={andIfCheckedChanged}
            disabled={false}
          >
            And If:
          </Checkbox>
        </div>

        <div
          className={cx(
            styles.addMargin,
            styles.sectionContainer,
            styles.andIfRow
          )}
        >
          <>
            Size (k)
            <GenericDropdownMenu
              selectedItem={sizeGreaterOrLessThan}
              setSelectedItem={setSizeGreaterOrLessThan}
              options={GreaterOrLessThanStrings}
              selectId={'AlertFilterSizeGreaterLessThan'}
              openRight={true}
              className={styles.andIfDropdown}
            />
            <input
              type="number"
              value={sizeHits}
              onChange={sizeHitsChanged}
              className={styles.sizePriceInput}
            />
            <div className={styles.andOr}>
              <GenericDropdownMenu
                selectedItem={priceSizeAndOrValue}
                setSelectedItem={setPriceSizeAndOrValue}
                options={AndOrStrings}
                selectId={'AlertFilterAndOr'}
                openRight={false}
                className={styles.andIfDropdown}
              />
            </div>
            <GenericDropdownMenu
              selectedItem={priceType}
              setSelectedItem={setPriceType}
              options={Object.keys(PriceType).filter((item) =>
                isNaN(Number(item))
              )}
              selectId={'AlertFilterAndOr'}
              openRight={false}
              className={styles.andIfDropdown}
            />
            <GenericDropdownMenu
              selectedItem={priceGreaterOrLessThan}
              setSelectedItem={setPriceGreaterOrLessThan}
              options={GreaterOrLessThanStrings}
              selectId={'AlertFilterPriceGreaterLessThan'}
              openRight={false}
              className={styles.andIfDropdown}
            />
            <input
              type="number"
              value={priceHits}
              onChange={priceHitsChanged}
              className={styles.sizePriceInput}
            />
          </>
        </div>
      </div>
      <ComponentFooter
        onCancelClick={closeFunc}
        onApplyClick={apply}
        applyId="alert-filter-editor-save"
        errorText={errorText}
      />
    </>
  )
}

interface CheckBoxCellProps {
  alertWhenNewBidOffer: boolean
  alertWhenNewBidOfferChanged: (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => void
  labelText: string
}
const WebOnlyCheckboxCell: FC<CheckBoxCellProps> = ({
  alertWhenNewBidOffer,
  alertWhenNewBidOfferChanged,
  labelText
}) => {
  const isOpenFin = isOpenFinRuntime()
  if (isOpenFin) return <></>
  return (
    <td>
      <Checkbox
        locator={labelText}
        checked={alertWhenNewBidOffer}
        onChange={alertWhenNewBidOfferChanged}
      >
        {labelText}
      </Checkbox>
    </td>
  )
}

export default AlertFilterEdit
